/**
 * CR204: Sort Bar Block
 * CR320: Wired to layout_json.sortOptions and context.selectedSort.
 *        Sort changes now propagate to ResultsBlock via context.
 * CR500: Now fully config-driven. Sort dropdown visibility is controlled by config.
 *        Removed hardcoded "X deals found" - now handled by ResultsMetaBlock.
 * 
 * Renders the sort controls based on layout configuration.
 */

import { useMemo } from "react";
import { ArrowUpDown } from "lucide-react";
import type { BlockProps } from "./types";
import type { SortBarConfig, SortOptionId } from "@/lib/layout-studio/types";
import { DEFAULT_SORT_BAR_CONFIG } from "@/lib/layout-studio/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SORT_LABELS: Record<string, string> = {
  newest: "Newest",
  best_value: "Best Value",
  expiring_soon: "Expiring Soon",
  merchant_az: "Merchant A-Z",
  category_az: "Category A-Z",
};

export function SortBarBlock({ config, context }: BlockProps<SortBarConfig>) {
  // CR500: Merge with defaults
  const mergedConfig: SortBarConfig = {
    ...DEFAULT_SORT_BAR_CONFIG,
    ...config,
  };

  // CR320: Get enabled sort options from layout, sorted by order
  const enabledSorts = useMemo(() => {
    return context.layout?.sortOptions
      ?.filter((s) => s.enabled)
      ?.sort((a, b) => a.order - b.order) || [
        { id: "newest", order: 0, enabled: true },
        { id: "best_value", order: 1, enabled: true },
        { id: "expiring_soon", order: 2, enabled: true },
        { id: "merchant_az", order: 3, enabled: true },
      ];
  }, [context.layout?.sortOptions]);

  // CR320: Handle sort change - propagate to context
  const handleSortChange = (value: string) => {
    context.onSortChange(value as SortOptionId);
  };

  // CR500: If dropdown is disabled, don't render anything
  if (!mergedConfig.showSortDropdown) {
    return null;
  }

  return (
    <div className="py-2 px-4 border-b flex items-center justify-end">
      <div className="flex items-center gap-2">
        {mergedConfig.showSortIcon && (
          <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
        )}
        {mergedConfig.showLabel && (
          <span className="text-xs text-muted-foreground">Sort by:</span>
        )}
        <Select 
          value={context.selectedSort} 
          onValueChange={handleSortChange}
        >
          <SelectTrigger className="h-7 text-xs w-auto min-w-[100px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card border-border z-50">
            {enabledSorts.map((s) => (
              <SelectItem key={s.id} value={s.id} className="text-xs">
                {SORT_LABELS[s.id] || s.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
